import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, webshopAdminNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Palette,
  Save,
  RotateCcw,
  Eye,
  ShoppingBag,
  Type,
  Layout,
  Sparkles,
  Loader2,
  Check,
  LayoutGrid
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Card style presets
const CARD_STYLES = {
  classic: {
    id: "classic",
    name: "Classic",
    description: "Clean and professional",
    borderRadius: "12px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-square",
    buttonStyle: "rounded-lg",
    priceSize: "text-xl",
    hoverEffect: "border-color"
  },
  modern: {
    id: "modern",
    name: "Modern",
    description: "Sleek with subtle shadows",
    borderRadius: "16px",
    shadow: "0 4px 20px rgba(0,0,0,0.3)",
    borderWidth: "0px",
    imageRatio: "aspect-[4/3]",
    buttonStyle: "rounded-xl",
    priceSize: "text-2xl",
    hoverEffect: "lift"
  },
  minimal: {
    id: "minimal",
    name: "Minimal",
    description: "Simple and elegant",
    borderRadius: "8px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-video",
    buttonStyle: "rounded-md",
    priceSize: "text-lg",
    hoverEffect: "opacity"
  },
  bold: {
    id: "bold",
    name: "Bold",
    description: "Eye-catching with strong colors",
    borderRadius: "20px",
    shadow: "0 8px 30px rgba(0,0,0,0.4)",
    borderWidth: "2px",
    imageRatio: "aspect-square",
    buttonStyle: "rounded-full",
    priceSize: "text-3xl",
    hoverEffect: "scale"
  },
  compact: {
    id: "compact",
    name: "Compact",
    description: "More products, less space",
    borderRadius: "8px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-[3/2]",
    buttonStyle: "rounded-md",
    priceSize: "text-base",
    hoverEffect: "border-color"
  },
  elegant: {
    id: "elegant",
    name: "Elegant",
    description: "Premium luxury feel",
    borderRadius: "4px",
    shadow: "0 2px 15px rgba(0,0,0,0.2)",
    borderWidth: "1px",
    imageRatio: "aspect-[3/4]",
    buttonStyle: "rounded-none",
    priceSize: "text-xl",
    hoverEffect: "glow"
  }
};

// Color picker component
const ColorPicker = ({ label, value, onChange, description }) => (
  <div className="space-y-2">
    <Label className="text-white">{label}</Label>
    {description && <p className="text-xs text-[#94A3B8]">{description}</p>}
    <div className="flex items-center gap-3">
      <div 
        className="w-10 h-10 rounded-lg border-2 border-white/20 cursor-pointer overflow-hidden"
        style={{ backgroundColor: value }}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-white/5 border-white/10 text-white font-mono"
        placeholder="#000000"
      />
    </div>
  </div>
);

// Preview card component with style support
const PreviewCard = ({ settings, compact = false }) => {
  const cardStyle = CARD_STYLES[settings.card_style] || CARD_STYLES.classic;
  
  const getHoverClass = () => {
    switch(cardStyle.hoverEffect) {
      case 'lift': return 'hover:-translate-y-2 hover:shadow-2xl';
      case 'scale': return 'hover:scale-105';
      case 'opacity': return 'hover:opacity-90';
      case 'glow': return 'hover:shadow-lg hover:shadow-current/20';
      default: return 'hover:border-teal-500/50';
    }
  };

  return (
    <div 
      className={`overflow-hidden border transition-all duration-300 ${getHoverClass()}`}
      style={{ 
        backgroundColor: settings.card_bg,
        borderColor: settings.card_border,
        borderRadius: cardStyle.borderRadius,
        boxShadow: cardStyle.shadow,
        borderWidth: cardStyle.borderWidth
      }}
    >
      <div 
        className={`${cardStyle.imageRatio} bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center`}
      >
        <ShoppingBag className={compact ? "w-8 h-8 text-gray-500" : "w-12 h-12 text-gray-500"} />
      </div>
      <div className={compact ? "p-3 space-y-2" : "p-4 space-y-3"}>
        <div 
          className="text-xs uppercase tracking-wider"
          style={{ color: settings.badge_category_bg }}
        >
          Category
        </div>
        <h3 style={{ color: settings.card_text }} className={compact ? "font-semibold text-sm" : "font-semibold"}>
          Sample Product
        </h3>
        {!compact && (
          <p style={{ color: settings.card_text_secondary }} className="text-sm">
            Product description goes here...
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ color: settings.price_color }} className={`${cardStyle.priceSize} font-bold`}>
            kr 599
          </span>
          <span style={{ color: settings.price_old_color }} className="line-through text-sm">
            kr 799
          </span>
          <span 
            className="px-2 py-0.5 rounded text-xs font-bold"
            style={{ 
              backgroundColor: settings.badge_sale_bg,
              color: settings.badge_sale_text
            }}
          >
            -25%
          </span>
        </div>
        <button
          className={`w-full ${compact ? 'py-1.5 text-sm' : 'py-2'} ${cardStyle.buttonStyle} font-semibold transition-all`}
          style={{
            background: `linear-gradient(to right, ${settings.btn_primary_from}, ${settings.btn_primary_to})`,
            color: settings.btn_primary_text
          }}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};

// Card Style Selector Component - All cards same size, only colors/styles differ
const CardStyleSelector = ({ selectedStyle, onSelect, settings }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    {Object.values(CARD_STYLES).map(style => (
      <div
        key={style.id}
        onClick={() => onSelect(style.id)}
        className={`relative cursor-pointer rounded-xl p-3 border-2 transition-all ${
          selectedStyle === style.id 
            ? 'border-[#23F7DD] bg-[#23F7DD]/10' 
            : 'border-white/10 hover:border-white/30 bg-white/5'
        }`}
      >
        {selectedStyle === style.id && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-[#23F7DD] rounded-full flex items-center justify-center z-10">
            <Check className="w-4 h-4 text-[#050505]" />
          </div>
        )}
        {/* Fixed size preview - all cards same dimensions */}
        <div className="h-48 overflow-hidden mb-3">
          <div 
            className="bg-[#0A0A0A] overflow-hidden h-full flex flex-col"
            style={{
              borderRadius: style.borderRadius,
              border: `${style.borderWidth} solid ${settings.card_border}`,
              boxShadow: style.shadow
            }}
          >
            {/* Fixed image area */}
            <div className="h-20 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-8 h-8 text-gray-500" />
            </div>
            {/* Content area */}
            <div className="p-2 space-y-1 flex-1 overflow-hidden">
              <div 
                className="text-[8px] uppercase tracking-wider"
                style={{ color: settings.badge_category_bg }}
              >
                Category
              </div>
              <h3 style={{ color: settings.card_text }} className="font-semibold text-xs truncate">
                Sample Product
              </h3>
              <div className="flex items-center gap-1 flex-wrap">
                <span style={{ color: settings.price_color }} className="text-sm font-bold">
                  kr 599
                </span>
                <span style={{ color: settings.price_old_color }} className="line-through text-[10px]">
                  kr 799
                </span>
              </div>
              <button
                className={`w-full py-1 text-[10px] ${style.buttonStyle} font-semibold`}
                style={{
                  background: `linear-gradient(to right, ${settings.btn_primary_from}, ${settings.btn_primary_to})`,
                  color: settings.btn_primary_text
                }}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
        <h4 className="text-white font-semibold text-sm">{style.name}</h4>
        <p className="text-[#94A3B8] text-xs">{style.description}</p>
      </div>
    ))}
  </div>
);

export default function AdminWebshopAppearance() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/webshop-appearance`);
      setSettings(res.data);
    } catch (err) {
      console.error("Error fetching appearance settings:", err);
      toast.error("Could not load appearance settings");
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/webshop-appearance`, settings);
      toast.success("Appearance settings saved!");
      setHasChanges(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all appearance settings to defaults?")) {
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/webshop-appearance/reset`);
      setSettings(res.data.settings);
      toast.success("Settings reset to defaults");
      setHasChanges(false);
    } catch (err) {
      console.error("Error resetting settings:", err);
      toast.error("Failed to reset settings");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    window.open("/webshop", "_blank");
  };

  if (loading) {
    return (
      <DashboardLayout navItems={webshopAdminNavItems} title="Webshop Appearance">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={webshopAdminNavItems} title="Webshop Appearance">
      <div className="space-y-6" data-testid="webshop-appearance">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
            <Palette className="w-8 h-8 text-[#F7931A]" />
            Webshop Appearance
          </h1>
          <p className="text-[#94A3B8] mt-1">
            Customize the look and feel of your public webshop
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={openPreview}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving}
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-0">
              <Tabs defaultValue="cards" className="w-full">
                <TabsList className="w-full bg-[#1A1A1A] rounded-none border-b border-white/10 p-1 flex-wrap">
                  <TabsTrigger value="cards" className="flex-1 data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
                    <LayoutGrid className="w-4 h-4 mr-2" />
                    Card Style
                  </TabsTrigger>
                  <TabsTrigger value="branding" className="flex-1 data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
                    <Type className="w-4 h-4 mr-2" />
                    Branding
                  </TabsTrigger>
                  <TabsTrigger value="colors" className="flex-1 data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
                    <Palette className="w-4 h-4 mr-2" />
                    Colors
                  </TabsTrigger>
                  <TabsTrigger value="buttons" className="flex-1 data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Buttons
                  </TabsTrigger>
                  <TabsTrigger value="layout" className="flex-1 data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
                    <Layout className="w-4 h-4 mr-2" />
                    Layout
                  </TabsTrigger>
                </TabsList>

                {/* Card Style Tab */}
                <TabsContent value="cards" className="p-6 space-y-6">
                  <div>
                    <h3 className="text-white font-semibold mb-2">Choose Card Style</h3>
                    <p className="text-[#94A3B8] text-sm mb-4">
                      Select how your product cards will look in the webshop
                    </p>
                    <CardStyleSelector 
                      selectedStyle={settings.card_style || 'classic'} 
                      onSelect={(style) => updateSetting('card_style', style)}
                      settings={settings}
                    />
                  </div>
                </TabsContent>

                {/* Branding Tab */}
                <TabsContent value="branding" className="p-6 space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white">Shop Name</Label>
                      <Input
                        value={settings.shop_name}
                        onChange={(e) => updateSetting("shop_name", e.target.value)}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="AllStillas Shop"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Tagline</Label>
                      <Input
                        value={settings.shop_tagline}
                        onChange={(e) => updateSetting("shop_tagline", e.target.value)}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="Professional Equipment"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Logo URL (optional)</Label>
                      <Input
                        value={settings.logo_url}
                        onChange={(e) => updateSetting("logo_url", e.target.value)}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Colors Tab */}
                <TabsContent value="colors" className="p-6 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Page & Header</h3>
                      <ColorPicker
                        label="Page Background"
                        value={settings.page_bg}
                        onChange={(v) => updateSetting("page_bg", v)}
                      />
                      <ColorPicker
                        label="Header Background"
                        value={settings.header_bg}
                        onChange={(v) => updateSetting("header_bg", v)}
                      />
                      <ColorPicker
                        label="Header Text"
                        value={settings.header_text}
                        onChange={(v) => updateSetting("header_text", v)}
                      />
                      <ColorPicker
                        label="Header Accent"
                        value={settings.header_accent}
                        onChange={(v) => updateSetting("header_accent", v)}
                      />
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Product Cards</h3>
                      <ColorPicker
                        label="Card Background"
                        value={settings.card_bg}
                        onChange={(v) => updateSetting("card_bg", v)}
                      />
                      <ColorPicker
                        label="Card Border"
                        value={settings.card_border}
                        onChange={(v) => updateSetting("card_border", v)}
                      />
                      <ColorPicker
                        label="Card Text"
                        value={settings.card_text}
                        onChange={(v) => updateSetting("card_text", v)}
                      />
                      <ColorPicker
                        label="Card Secondary Text"
                        value={settings.card_text_secondary}
                        onChange={(v) => updateSetting("card_text_secondary", v)}
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-6 pt-4">
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Prices</h3>
                      <ColorPicker
                        label="Price Color"
                        value={settings.price_color}
                        onChange={(v) => updateSetting("price_color", v)}
                      />
                      <ColorPicker
                        label="Old Price Color"
                        value={settings.price_old_color}
                        onChange={(v) => updateSetting("price_old_color", v)}
                      />
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Badges</h3>
                      <ColorPicker
                        label="Sale Badge Background"
                        value={settings.badge_sale_bg}
                        onChange={(v) => updateSetting("badge_sale_bg", v)}
                      />
                      <ColorPicker
                        label="Sale Badge Text"
                        value={settings.badge_sale_text}
                        onChange={(v) => updateSetting("badge_sale_text", v)}
                      />
                      <ColorPicker
                        label="Category Badge Color"
                        value={settings.badge_category_bg}
                        onChange={(v) => updateSetting("badge_category_bg", v)}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Buttons Tab */}
                <TabsContent value="buttons" className="p-6 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Primary Button (Add to Cart)</h3>
                      <ColorPicker
                        label="Gradient Start"
                        value={settings.btn_primary_from}
                        onChange={(v) => updateSetting("btn_primary_from", v)}
                      />
                      <ColorPicker
                        label="Gradient End"
                        value={settings.btn_primary_to}
                        onChange={(v) => updateSetting("btn_primary_to", v)}
                      />
                      <ColorPicker
                        label="Text Color"
                        value={settings.btn_primary_text}
                        onChange={(v) => updateSetting("btn_primary_text", v)}
                      />
                      
                      {/* Preview button */}
                      <div className="pt-4">
                        <Label className="text-white mb-2 block">Preview:</Label>
                        <button
                          className="w-full py-3 rounded-lg font-semibold transition-all"
                          style={{
                            background: `linear-gradient(to right, ${settings.btn_primary_from}, ${settings.btn_primary_to})`,
                            color: settings.btn_primary_text
                          }}
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Secondary Button</h3>
                      <ColorPicker
                        label="Background"
                        value={settings.btn_secondary_bg}
                        onChange={(v) => updateSetting("btn_secondary_bg", v)}
                      />
                      <ColorPicker
                        label="Border"
                        value={settings.btn_secondary_border}
                        onChange={(v) => updateSetting("btn_secondary_border", v)}
                      />
                      <ColorPicker
                        label="Text Color"
                        value={settings.btn_secondary_text}
                        onChange={(v) => updateSetting("btn_secondary_text", v)}
                      />
                      
                      {/* Preview button */}
                      <div className="pt-4">
                        <Label className="text-white mb-2 block">Preview:</Label>
                        <button
                          className="w-full py-3 rounded-lg font-semibold transition-all border"
                          style={{
                            backgroundColor: settings.btn_secondary_bg,
                            borderColor: settings.btn_secondary_border,
                            color: settings.btn_secondary_text
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Layout Tab */}
                <TabsContent value="layout" className="p-6 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Hero Section</h3>
                      <ColorPicker
                        label="Gradient Start"
                        value={settings.hero_gradient_from}
                        onChange={(v) => updateSetting("hero_gradient_from", v)}
                      />
                      <ColorPicker
                        label="Gradient Middle"
                        value={settings.hero_gradient_via}
                        onChange={(v) => updateSetting("hero_gradient_via", v)}
                      />
                      <ColorPicker
                        label="Gradient End"
                        value={settings.hero_gradient_to}
                        onChange={(v) => updateSetting("hero_gradient_to", v)}
                      />
                      <ColorPicker
                        label="Hero Text"
                        value={settings.hero_text}
                        onChange={(v) => updateSetting("hero_text", v)}
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-white font-semibold border-b border-white/10 pb-2">Search Bar</h3>
                      <ColorPicker
                        label="Background"
                        value={settings.search_bg}
                        onChange={(v) => updateSetting("search_bg", v)}
                      />
                      <ColorPicker
                        label="Border"
                        value={settings.search_border}
                        onChange={(v) => updateSetting("search_border", v)}
                      />
                      <ColorPicker
                        label="Text"
                        value={settings.search_text}
                        onChange={(v) => updateSetting("search_text", v)}
                      />
                      <ColorPicker
                        label="Placeholder"
                        value={settings.search_placeholder}
                        onChange={(v) => updateSetting("search_placeholder", v)}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4 pt-4">
                    <h3 className="text-white font-semibold border-b border-white/10 pb-2">Custom CSS (Advanced)</h3>
                    <textarea
                      value={settings.custom_css}
                      onChange={(e) => updateSetting("custom_css", e.target.value)}
                      className="w-full h-32 bg-white/5 border border-white/10 rounded-lg p-3 text-white font-mono text-sm"
                      placeholder="/* Add custom CSS here */"
                    />
                    <p className="text-xs text-[#94A3B8]">
                      Add custom CSS to further customize the webshop. Use with caution.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Live Preview */}
        <div className="lg:col-span-1">
          <Card className="bg-[#0A0A0A] border-white/10 sticky top-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#23F7DD]" />
                Live Preview
              </CardTitle>
              <CardDescription className="text-[#94A3B8]">
                See how your changes will look
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="rounded-xl p-4"
                style={{ backgroundColor: settings.page_bg }}
              >
                <PreviewCard settings={settings} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </DashboardLayout>
  );
}
